import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Compass, Info, QrCode, UserRound } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { supabase } from "../supabaseClient";
import api from "../api";

function normalizeToArray(raw) {
    if (Array.isArray(raw)) return raw;
    if (Array.isArray(raw?.data)) return raw.data;
    if (Array.isArray(raw?.passes)) return raw.passes;
    return [];
}

function passStatusLabel(pass) {
    const status = String(pass?.status || "").toLowerCase();
    const today = new Date().toISOString().slice(0, 10);
    const start = String(pass?.start_date || "");
    const end = String(pass?.end_date || "");
    const plannedStart = String(pass?.planned_start_date || "");
    const plannedEnd = String(pass?.planned_end_date || "");

    if (status === "unactivated" || !start || !end) {
        if (plannedStart && today < plannedStart) return "Trip Ready";
        if (plannedEnd && today > plannedEnd) return "Trip Window Passed";
        return "Ready to Activate";
    }

    if (today < start) return "Not Redeemable Yet";
    if (today > end) return "Expired";
    return "Active";
}

function formatDate(ymd) {
    if (!ymd) return "Activates on first scan";
    const [y, m, d] = String(ymd).split("-").map(Number);
    if (!y || !m || !d) return String(ymd);
    return `${m}/${d}/${y}`;
}

function titleCaseName(value) {
    const raw = String(value || "")
        .replace(/[._-]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();

    if (!raw) return "";

    const first = raw
        .split(" ")[0]
        .replace(/\d+$/g, "")
        .replace(/[^a-zA-Z'’]/g, "")
        .trim();

    if (!first) return "";
    return first.charAt(0).toUpperCase() + first.slice(1).toLowerCase();
}

function getFirstName(user, profile) {
    const meta = user?.user_metadata || {};

    const candidates = [
        profile?.first_name,
        meta.first_name,
        meta.given_name,
        meta.full_name,
        meta.name,
        profile?.username,
        user?.email?.split("@")[0],
    ];

    for (const candidate of candidates) {
        const name = titleCaseName(candidate);
        if (name) return name;
    }

    return "Beach Bum";
}

function passQrValue(pass) {
    if (!pass?.id) return "";
    return JSON.stringify({ pass_uuid: pass.id });
}


function QuickAction({ icon, title, sub, onClick }) {
    return (
        <button className="apphome-action" type="button" onClick={onClick}>
            <span className="apphome-action__icon" aria-hidden="true">{icon}</span>
            <span className="apphome-action__copy">
                <strong>{title}</strong>
                <small>{sub}</small>
            </span>
        </button>
    );
}

function AppHomePassModal({ pass, onClose }) {
    if (!pass?.id) return null;

    const handleClose = (event) => {
        event?.preventDefault?.();
        event?.stopPropagation?.();
        onClose?.();
    };

    const status = passStatusLabel(pass);
    const holderLabel = pass.holder_name || "Beach Bum Pass";
    const passName = pass?.pass_types?.name || "Beach Bum Pass";
    const tripWindow = pass?.planned_start_date && pass?.planned_end_date
        ? `${formatDate(pass.planned_start_date)} → ${formatDate(pass.planned_end_date)}`
        : "Trip window not set";
    const activeWindow = pass?.start_date && pass?.end_date
        ? `${formatDate(pass.start_date)} → ${formatDate(pass.end_date)}`
        : "Activates on first scan";

    return (
        <div className="pass-modal-backdrop apphome-pass-modal-backdrop" role="presentation" onClick={handleClose}>
            <div
                className="pass-modal-card pass-modal-card--app apphome-pass-modal-card"
                role="dialog"
                aria-modal="true"
                aria-label={`${passName} details`}
                onClick={(event) => event.stopPropagation()}
                onPointerDown={(event) => event.stopPropagation()}
            >
                <button
                    className="pass-modal-close"
                    type="button"
                    onPointerDown={(event) => event.stopPropagation()}
                    onClick={handleClose}
                    aria-label="Close pass details"
                >
                    ×
                </button>

                <div className="pass-modal-head apphome-pass-modal-head">
                    <div>
                        <div className="passes-web-badge">Your Beach Bum Pass</div>
                        <h2>{holderLabel}</h2>
                        <p>{passName}</p>
                    </div>
                    <div className="managed-pass-status">{status}</div>
                </div>

                <div className="pass-modal-body apphome-pass-modal-body">
                    <div className="pass-modal-qr-shell apphome-pass-modal-qr">
                        <QRCodeSVG value={passQrValue(pass)} size={230} includeMargin />
                        <div className="pass-modal-qr-note">Show this QR when this specific pass is being scanned.</div>
                    </div>

                    <div className="managed-pass-meta pass-modal-meta apphome-pass-modal-meta">
                        <div>
                            <span>Trip Window</span>
                            <strong>{tripWindow}</strong>
                        </div>
                        <div>
                            <span>Active Window</span>
                            <strong>{activeWindow}</strong>
                        </div>
                        <div>
                            <span>Pass ID</span>
                            <strong className="mono">{pass.id}</strong>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default function AppHome() {
    const nav = useNavigate();
    const [user, setUser] = useState(null);
    const [profile, setProfile] = useState(null);
    const [passes, setPasses] = useState([]);
    const [selectedHomePass, setSelectedHomePass] = useState(null);

    useEffect(() => {
        let alive = true;

        const load = async () => {
            const { data } = await supabase.auth.getSession();
            if (!alive) return;
            const u = data?.session?.user ?? null;
            setUser(u);

            if (u?.email) {
                try {
                    const profileRes = await api.get("/profile");
                    if (!alive) return;
                    setProfile(profileRes?.data?.profile || null);
                } catch (e) {
                    console.warn("AppHome profile fetch failed:", e?.response?.data || e?.message || e);
                    if (alive) setProfile(null);
                }

                try {
                    const res = await api.get(`/passes?email=${encodeURIComponent(u.email)}`);
                    if (!alive) return;
                    setPasses(normalizeToArray(res?.data));
                } catch (e) {
                    console.error("AppHome passes fetch failed:", e);
                    if (alive) setPasses([]);
                }
            }
        };

        load();
        return () => {
            alive = false;
        };
    }, []);

    const displayName = useMemo(() => getFirstName(user, profile), [user, profile]);

    const featuredPass = useMemo(() => {
        if (!Array.isArray(passes) || passes.length === 0) return null;
        const adultPasses = passes.filter((pass) => String(pass?.holder_type || "adult").toLowerCase() !== "child");
        return adultPasses[0] || passes[0];
    }, [passes]);

    const hasPass = Boolean(featuredPass?.id);
    const qrValue = hasPass ? passQrValue(featuredPass) : "";

    return (
        <div className="apphome apphome-screen">
            <section className="apphome-welcome-card apphome-welcome-card--hero-first" aria-label="Beach Bum Pass app home">
                <div className="apphome-fred-hero" aria-hidden="true">
                    <img src="/assets/Graphics/Fred_Showing_Pass.png" alt="" />
                </div>

                <div className="apphome-welcome-copy">
                    <p className="apphome-welcome-kicker">Beach Bum Pass</p>
                    <h1>
                        Welcome Ashore,
                        <span>{displayName}</span>
                    </h1>
                </div>
            </section>

            <section className={`apphome-qr-card ${hasPass ? "has-pass" : "is-empty"}`} aria-label="Home pass QR code">
                <div className="apphome-qr-card__copy">
                    <span className="apphome-mini-label">Home QR</span>
                    <h2>{hasPass ? "Your Beach Bum Pass" : "Your QR shows up after checkout."}</h2>
                    <p>
                        {hasPass
                            ? "Pull this up at participating stops so vendors can scan your featured pass."
                            : "Once your pass is built, your scan-ready QR code will live right here on Home."}
                    </p>
                </div>

                <div className="apphome-qr-card__display">
                    {hasPass ? (
                        <div className="apphome-qr-frame">
                            <QRCodeSVG value={qrValue} size={184} includeMargin />
                        </div>
                    ) : (
                        <div className="apphome-qr-placeholder" aria-hidden="true">
                            <QrCode size={46} />
                            <span>QR</span>
                        </div>
                    )}

                    <button
                        className="apphome-qr-link"
                        type="button"
                        onClick={() => (hasPass ? setSelectedHomePass(featuredPass) : nav("/my-pass"))}
                    >
                        {hasPass ? "Open full pass" : "Build pass"}
                    </button>
                </div>
            </section>

            <section className="apphome-action-grid" aria-label="Quick actions">
                <QuickAction
                    icon={<Compass size={19} />}
                    title="Explore"
                    sub="Find the next stop"
                    onClick={() => nav("/discover")}
                />
                <QuickAction
                    icon={<QrCode size={19} />}
                    title="Scan Pass"
                    sub="Open QR access"
                    onClick={() => nav("/my-pass")}
                />
                <QuickAction
                    icon={<Info size={19} />}
                    title="How It Works"
                    sub="Quick app rundown"
                    onClick={() => nav("/how-it-works")}
                />
                <QuickAction
                    icon={<UserRound size={19} />}
                    title="Account"
                    sub="Crew, billing, help"
                    onClick={() => nav("/account")}
                />
            </section>

            <AppHomePassModal pass={selectedHomePass} onClose={() => setSelectedHomePass(null)} />
        </div>
    );
}
